import React, { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import Copy from 'Src/components/Copy';
import { Flex } from 'Src/components/layout/Flex';
import { useLogger } from 'Src/feature/context-logger/useLogger';
import { Divider } from 'Src/feature/divider/Divider';
import { FormField } from 'Src/feature/form/FormField';
import { Modal } from 'Src/feature/modal/Modal';
import { ModalAction } from 'Src/feature/modal/ModalAction';
import { ModalActions } from 'Src/feature/modal/ModalActions';
import { ModalContent } from 'Src/feature/modal/ModalContent';
import { ModalHeader } from 'Src/feature/modal/ModalHeader';
import { ModalTitle } from 'Src/feature/modal/ModalTitle';

import { OpenFeeModal } from './OpenFeeModal';
import { useCheckFeesModal } from './useCheckFeesModal';

import styles from './CheckFeesModal.module.scss';

const sectionId = 'CheckFeesModal';

type CheckFeesModalProps = {
  open: boolean;
  onClose: () => void;
}

const CheckFeesModalContent: FC<CheckFeesModalProps> = ({ onClose }) => {
  const log = useLogger().getSubLogger({ name: sectionId });
  const { t } = useTranslation();
  const store = useCheckFeesModal();

  return (
    <>
      <ModalHeader>
        <ModalTitle copy={t('checkFees.labels.title')} />
      </ModalHeader>
      <ModalContent className={styles.content}>
        {(store.applicableAutoFees.length > 0) && (
          <Flex gap={5} className={styles.autoFee}>
            <Flex gap={1} className={styles.autoFee}>
              <Divider variant="stubby" />
              <Copy variant="body" size="lg" value={t('checkFees.labels.autoApplied')} />
            </Flex>
            <div className={styles.feeGrid}>
              {store.applicableAutoFees.map(({ fee, checkFee }) => (
                <Fragment key={fee.id}>
                  <div>
                    <Copy variant="body" value={fee.posName ? fee.posName : fee.name} component="p" />
                    <Copy
                      size="sm"
                      value={t('checkFees.labels.feeValue', {
                        context: fee.type === 'FixedAmount' ? 'amount' : fee.type === 'FixedPercent' ? 'percent' : 'open',
                        value: fee.fixedValue,
                      })}
                      component="p"
                    />
                  </div>
                  <Copy className={styles.appliedValue} variant="body" color={checkFee?.status === 'Disabled' ? 'textDisabled' : undefined} value={t('labels.util.currency', { value: checkFee?.status === 'Enabled' ? checkFee.appliedValue : 0 })} />
                  <FormField
                    type="switch"
                    checked={!checkFee?.isAutoDisabled}
                    onChange={(evt, value) => {
                      if (typeof store.checkId !== 'number') return log.warn('auto fee', 'onChange', '!checkId');
                      if (!store.updateAutoFee.isPending) store.handleAutoFeeChange({ checkId: store.checkId, feeId: fee.id, isAutoDisabled: !value });
                    }}
                  />
                  <Divider className={styles.divider} color="dark" />
                </Fragment>
              ))}
            </div>
          </Flex>
        )}
        <Flex gap={5}>
          {(store.applicableFees.length > 0) && (
            <Flex gap={1}>
              <Divider variant="stubby" />
              <Copy variant="body" size="lg" value={t('checkFees.labels.title')} />
            </Flex>
          )}
          <div className={styles.feeGrid}>
            {store.applicableFees.map(({ fee, checkFee }) => (
              <Fragment key={fee.id}>
                <div>
                  <Copy variant="body" value={fee.posName ? fee.posName : fee.name} component="p" />
                  <Copy
                    size="sm"
                    value={t('checkFees.labels.feeValue', {
                      context: fee.type === 'FixedAmount' ? 'amount' : fee.type === 'FixedPercent' ? 'percent' : 'open',
                      value: fee.fixedValue,
                    })}
                    component="p"
                  />
                </div>
                <Copy className={styles.appliedValue} variant="body" color={!checkFee ? 'textDisabled' : undefined} value={t('labels.util.currency', { value: checkFee ? checkFee.appliedValue : 0 })} />
                <FormField
                  type="numberStepper"
                  size="sm"
                  value={checkFee ? checkFee.quantity : 0}
                  min={0}
                  onChange={(evt, value) => store.handleFeeQtyChange(value, fee)}
                  disabled={fee.doAutoApply}
                />
                <Divider className={styles.divider} color="dark" />
              </Fragment>
            ))}
          </div>
        </Flex>
        <Flex gap={5}>
          {(store.applicableGratuities.length > 0) && (
            <Flex gap={1}>
              <Divider variant="stubby" />
              <Copy variant="body" size="lg" value={t('checkFees.labels.gratuity')} />
            </Flex>
          )}
          <div className={styles.feeGrid}>
            {store.applicableGratuities.map(({ fee, checkFee }) => (
              <Fragment key={fee.id}>
                <div>
                  <Copy variant="body" value={fee.posName ? fee.posName : fee.name} component="p" />
                  <Copy
                    size="sm"
                    value={t('checkFees.labels.feeValue', {
                      context: fee.type === 'FixedAmount' ? 'amount' : fee.type === 'FixedPercent' ? 'percent' : 'open',
                      value: fee.fixedValue,
                    })}
                    component="p"
                  />
                </div>
                <Copy className={styles.appliedValue} variant="body" color={!checkFee ? 'textDisabled' : undefined} value={t('labels.util.currency', { value: checkFee ? checkFee.appliedValue : 0 })} />
                <FormField
                  type="switch"
                  checked={!!checkFee}
                  onChange={() => {
                    if (!store.updateFee.isPending) store.handleGratuityChange(fee);
                  }}
                  disabled={fee.doAutoApply}
                />
                <Divider className={styles.divider} color="dark" />
              </Fragment>
            ))}
          </div>
        </Flex>
      </ModalContent>
      <ModalActions>
        <ModalAction color="secondary" label={t('labels.done')} onClick={onClose} />
      </ModalActions>
      <OpenFeeModal
        key={store.openFeeValues?.feeId}
        open={store.modal.openFee.isOpen}
        feeId={store.openFeeValues?.feeId ?? 0}
        onClose={store.modal.openFee.close}
        onSave={store.handleOpenFeeConfirm}
      />
    </>
  );
};

// eslint-disable-next-line react/no-multi-comp
const CheckFeesModal: FC<CheckFeesModalProps> = ({ open, onClose }) => (
  <Modal data-testid={sectionId} open={open}>
    <CheckFeesModalContent open={open} onClose={onClose} />
  </Modal>
);

export { CheckFeesModal };
